// ─── Event Names ────────────────────────────────────────────────────
// Single source of truth for all event names and domain topics.
// Format: DOMAIN.ACTION — always dot-separated, uppercase.
//
// Topics are grouped by domain (NEXUS.USERS, NEXUS.TENANTS, etc.).
// Event routing happens via the `eventType` message attribute, so
// adding new events never requires provisioning new Pub/Sub topics.
//
// Services use these constants when publishing/subscribing so typos
// are caught at compile time rather than at runtime.

// ── Opportunities ───────────────────────────────────────────────────

export const OpportunityEvents = {
    GENERATE_NEW_SET_REQUESTED: 'OPPORTUNITY.GENERATE_NEW_SET_REQUESTED',
    GENERATE_REQUESTED: 'OPPORTUNITY.GENERATE_REQUESTED',
    STORYBOARD_GENERATION_REQUESTED: 'OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED',
    AI_EDIT_SCRIPT_REQUESTED: 'OPPORTUNITY.AI_EDIT_SCRIPT_REQUESTED',
    SUGGEST_SCRIPT_REQUESTED: 'OPPORTUNITY.SUGGEST_SCRIPT_REQUESTED',
    RESOLVE_REFERENCE_LINKS_REQUESTED: 'OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED',
    GENERATED: 'OPPORTUNITY.GENERATED',
    SCORED: 'OPPORTUNITY.SCORED',
    APPROVED: 'OPPORTUNITY.APPROVED',
    REJECTED: 'OPPORTUNITY.REJECTED',
    BULK_REJECT_REQUESTED: 'OPPORTUNITY.BULK_REJECT_REQUESTED',
    EXPORT_REQUESTED: 'OPPORTUNITY.EXPORT_REQUESTED',
} as const;

export type OpportunityEventName = (typeof OpportunityEvents)[keyof typeof OpportunityEvents];

// ── Users ───────────────────────────────────────────────────────────

export const UserEvents = {
    REGISTERED: 'USER.REGISTERED',
    UPDATED: 'USER.UPDATED',
    DEACTIVATED: 'USER.DEACTIVATED',
    REACTIVATED: 'USER.REACTIVATED',
    ACCESS_GRANTED: 'USER.ACCESS_GRANTED',
    ACCESS_REVOKED: 'USER.ACCESS_REVOKED',
    ACCESS_BULK_GRANTED: 'USER.ACCESS_BULK_GRANTED',
    ACCESS_BULK_REVOKED: 'USER.ACCESS_BULK_REVOKED',
    ACCESS_REPLACED: 'USER.ACCESS_REPLACED',
} as const;

export type UserEventName = (typeof UserEvents)[keyof typeof UserEvents];

// ── Tenants ─────────────────────────────────────────────────────────

export const TenantEvents = {
    PROVISIONED: 'TENANT.PROVISIONED',
    UPDATED: 'TENANT.UPDATED',
    DEACTIVATED: 'TENANT.DEACTIVATED',
    /** Admin-initiated restore — clears `deletedAt` so the org is visible/usable again. */
    REACTIVATED: 'TENANT.REACTIVATED',
    /** Bulk-import tenant settings: upsert industries, wire to tenant, update defaults. */
    IMPORT_SETTINGS: 'TENANT.IMPORT_SETTINGS',
} as const;

export type TenantEventName = (typeof TenantEvents)[keyof typeof TenantEvents];

// ── Access Groups ───────────────────────────────────────────────────

export const AccessEvents = {
    CREATED: 'ACCESS.CREATED',
    BULK_CREATED: 'ACCESS.BULK_CREATED',
    UPDATED: 'ACCESS.UPDATED',
    DELETED: 'ACCESS.DELETED',
    /** Admin-initiated restore — clears `deletedAt` on a previously soft-deleted role. */
    RESTORED: 'ACCESS.RESTORED',
    PERMISSIONS_UPDATED: 'ACCESS.PERMISSIONS_UPDATED',
} as const;

export type AccessEventName = (typeof AccessEvents)[keyof typeof AccessEvents];

// ── Invites ─────────────────────────────────────────────────────────
// Invite flows are synchronous (the API route does the DB write itself)
// — these events are fire-and-forget for audit-only consumers.

export const InviteEvents = {
    CREATED:     'INVITE.CREATED',
    REGENERATED: 'INVITE.REGENERATED',
    /** Same token, fresh email send — distinct from REGENERATED so audit can read "resent the email" vs "issued a new link". */
    RESENT:      'INVITE.RESENT',
    /** Admin-revoked pending invite (token expired immediately, no acceptance possible). */
    REVOKED:     'INVITE.REVOKED',
} as const;

export type InviteEventName = (typeof InviteEvents)[keyof typeof InviteEvents];

// ── System ──────────────────────────────────────────────────────────

export const SystemEvents = {
    HEALTH_PING: 'SYSTEM.HEALTH_PING',
    /**
     * Asks a worker to turn a deploy's commit list into user-facing release
     * notes. Published by the API, which has already read GitHub and persisted
     * the changelog entry.
     */
    CHANGELOG_GENERATE_REQUESTED: 'SYSTEM.CHANGELOG_GENERATE_REQUESTED',
    /**
     * Asks a worker to deliver approved release notes to the chosen channels.
     * Published by the API once an admin has approved the notes and picked
     * where they go.
     */
    CHANGELOG_DELIVER_REQUESTED: 'SYSTEM.CHANGELOG_DELIVER_REQUESTED',
    /**
     * Asks a worker to merge the items an admin picked across several deploys
     * into one curated roundup. Separate from the per-deploy generation because
     * the input is a human's selection rather than a commit range.
     */
    CHANGELOG_DIGEST_GENERATE_REQUESTED: 'SYSTEM.CHANGELOG_DIGEST_GENERATE_REQUESTED',
    /** Asks a worker to deliver an approved digest to the mailing list. */
    CHANGELOG_DIGEST_DELIVER_REQUESTED: 'SYSTEM.CHANGELOG_DIGEST_DELIVER_REQUESTED',
    /**
     * Asks the API to look at GitHub for new production releases and to let
     * out any unattended changelog work that has come due (held Slack posts,
     * in-app notices, roundups). Published by Cloud Scheduler job
     * changelog-sync every ten minutes, which the API creates on boot when
     * SCHEDULER_BACKEND=gcp. The GitHub token lives on the API, so this event
     * is consumed there rather than on the users worker.
     */
    CHANGELOG_SYNC_REQUESTED: 'SYSTEM.CHANGELOG_SYNC_REQUESTED',
    /**
     * Asks a worker to run the admin ops sweep: prune event and token-usage
     * history past their retention windows, and raise the Slack alerts the ops
     * settings ask for. Published by Cloud Scheduler on a cron, because an
     * alert that only evaluates when an admin opens a tab cannot fire on the
     * quiet weekend it exists for. Every write the sweep performs happens on
     * the worker, so the API routes that observe history stay read-only.
     */
    ADMIN_OPS_SWEEP_REQUESTED: 'SYSTEM.ADMIN_OPS_SWEEP_REQUESTED',
} as const;

export type SystemEventName = (typeof SystemEvents)[keyof typeof SystemEvents];

// ── Auth ─────────────────────────────────────────────────────────────

export const AuthEvents = {
    LOGIN_SUCCEEDED: 'AUTH.LOGIN_SUCCEEDED',
    LOGOUT:          'AUTH.LOGOUT',
    MFA_ENABLED:     'AUTH.MFA_ENABLED',
    MFA_DISABLED:    'AUTH.MFA_DISABLED',
} as const;

export type AuthEventName = (typeof AuthEvents)[keyof typeof AuthEvents];

// ── Sync (Data Sync) ────────────────────────────────────────────────

export const SyncEvents = {
    ALL_REQUESTED: 'DATA_SYNC.ALL_REQUESTED',
    TENANT_SYNC_REQUESTED: 'DATA_SYNC.TENANT_SYNC_REQUESTED',
    TENANT_KPI_PRECACHE_REQUESTED: 'DATA_SYNC.TENANT_KPI_PRECACHE_REQUESTED',
} as const;

export type SyncEventName = (typeof SyncEvents)[keyof typeof SyncEvents];

// ── Pre Flight ──────────────────────────────────────────────────────

export const PreflightEvents = {
    REPORT_REQUESTED: 'PREFLIGHT.REPORT_REQUESTED',
    SCORE_ASSET: 'PREFLIGHT.SCORE_ASSET',
    UPLOAD_CONFIRMED: 'PREFLIGHT.UPLOAD_CONFIRMED',
    // Pre Flight – Scripts: async video-vs-script review pipeline. Hosted in the
    // preflight worker as its own event on the shared NEXUS.PREFLIGHT topic.
    SCRIPT_REVIEW_REQUESTED: 'PREFLIGHT.SCRIPT_REVIEW_REQUESTED',
    // Pre Flight – Scripts: an AI comment was deleted WITH a reason — the worker
    // drafts a prompt-improvement suggestion for the ticket.
    SCRIPT_REVIEW_COMMENT_REJECTED: 'PREFLIGHT.SCRIPT_REVIEW_COMMENT_REJECTED',
    // Pre Flight – Scripts: transcribe an uploaded video and pick which script in
    // the selected set it was made from (the creation form's auto-match).
    SCRIPT_MATCH_REQUESTED: 'PREFLIGHT.SCRIPT_MATCH_REQUESTED',
} as const;

export type PreflightEventName = (typeof PreflightEvents)[keyof typeof PreflightEvents];

// ── Workspace ────────────────────────────────────────────────────────

export const WorkspaceEvents = {
    DATA_REFRESH_REQUESTED: 'WORKSPACE.DATA_REFRESH_REQUESTED',
    DATA_REFRESHED:         'WORKSPACE.DATA_REFRESHED',
    /** Lightweight filter-option lookups (group-by distinct values, my-brand filter
     *  tuples) — dispatched instead of the API calling BigQuery directly. Routes to
     *  NEXUS.WORKSPACE by the WORKSPACE prefix; no new topic. */
    OPTIONS_REQUESTED:      'WORKSPACE.OPTIONS_REQUESTED',
} as const;

export type WorkspaceEventName = (typeof WorkspaceEvents)[keyof typeof WorkspaceEvents];

// ── Personas ─────────────────────────────────────────────────────────

export const PersonaEvents = {
    /** Amber Path: API asks the personas worker to discover the next batch of personas. */
    DISCOVER_REQUESTED: 'PERSONA.DISCOVER_REQUESTED',
    /**
     * Published by the upstream DS analysis pipeline (not Nexus) once a Tenant's
     * regenerated personalities are ready in BigQuery. Preferred payload uses
     * `session_id` (onboarding batch UUID); `tenantId` alone remains a compat
     * path. Nexus documents/consumes this event only — see data-sync's
     * persona-feedback-ready handler, which re-syncs the Tenant then marks the
     * in-scope unprocessed RejectedPersona rows processed.
     */
    FEEDBACK_READY: 'PERSONA.FEEDBACK_READY',
    /**
     * Published by Nexus when the reviewer hits "Go to my personas" — not "Finish
     * setup", which only commits the industry selection. Signals adge_backend / DS
     * to recalculate core personalities from unprocessed RejectedPersona rows, and
     * is skipped when the completion adds none since the tenant's last one.
     */
    ONBOARDING_COMPLETED: 'PERSONA.ONBOARDING_COMPLETED',
    /**
     * Child EventJob for the core/brand persona re-sync that FEEDBACK_READY
     * runs in-process. Not published on Pub/Sub — recorded so the Events
     * action shows completed → ready → core → comp.
     */
    CORE_SYNC: 'PERSONA.CORE_SYNC',
    /** Same as CORE_SYNC for the industry (comp) persona re-sync. */
    INDUSTRY_SYNC: 'PERSONA.INDUSTRY_SYNC',
    /** Requests AI avatar generation (A2) for one persona; published on create when `avatarUploadId` is null. */
    AVATAR_GENERATION_REQUESTED: 'PERSONA.AVATAR_GENERATION_REQUESTED',
} as const;

export type PersonaEventName = (typeof PersonaEvents)[keyof typeof PersonaEvents];

// ── Persona Opportunities (`persona_opportunities` flow) ─────────────
//
// Event *names* for the Create Opportunity Set flow (PRD).
// Payload shapes live in `events/persona-opportunities.ts` — pair each schema
// with a constant below.
//
// Add one entry here per async action that crosses service boundaries
// (e.g. API → worker). Format: `PERSONA_OPPORTUNITY.<VERB_NOUN>`.

export const PersonaOpportunityEvents = {
    /** Fired when the user taps Create & Generate. */
    GENERATE_SET_REQUESTED: 'PERSONA_OPPORTUNITY.GENERATE_SET_REQUESTED',
    /** Fired when the user generates (or regenerates) scripts for persona opportunities. */
    GENERATE_SCRIPTS_REQUESTED: 'PERSONA_OPPORTUNITY.GENERATE_SCRIPTS_REQUESTED',
    /** Fired when the user asks for three alternative hooks on one persona script. */
    GENERATE_HOOK_VARIATIONS_REQUESTED: 'PERSONA_OPPORTUNITY.GENERATE_HOOK_VARIATIONS_REQUESTED',
    /** Fired when the user AI-edits one or more persona scripts. */
    AI_EDIT_SCRIPTS_REQUESTED: 'PERSONA_OPPORTUNITY.AI_EDIT_SCRIPTS_REQUESTED',
    /** Fired when the user AI-edits one persona brief. */
    AI_EDIT_BRIEFS_REQUESTED: 'PERSONA_OPPORTUNITY.AI_EDIT_BRIEFS_REQUESTED',
    /** Fired when the user generates a sketch storyboard for one persona script. */
    STORYBOARD_GENERATION_REQUESTED: 'PERSONA_OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED',
    /** Fired when the user submits Add your script (custom brief → N scripts). */
    SUGGEST_SCRIPTS_REQUESTED: 'PERSONA_OPPORTUNITY.SUGGEST_SCRIPTS_REQUESTED',
    /** Fired when the user confirms Import (paste/file text → one PersonaScript). */
    IMPORT_SCRIPT_FROM_TEXT_REQUESTED: 'PERSONA_OPPORTUNITY.IMPORT_SCRIPT_FROM_TEXT_REQUESTED',
    /** Fired when the Suggest Script modal pre-fetches pasted reference URLs. */
    RESOLVE_REFERENCE_LINKS_REQUESTED: 'PERSONA_OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED',
} as const;

export type PersonaOpportunityEventName =
    (typeof PersonaOpportunityEvents)[keyof typeof PersonaOpportunityEvents];

// ── Beta Features ───────────────────────────────────────────────────
// Shared Beta Worker domain. One topic for every Beta Feature that has
// backend/business logic. Frontend-only features add no event here.
// New backend features add an event name, never a new topic.

export const BetaFeatureEvents = {
    // New backend features add an event name here, never a new topic.
} as const;

export type BetaFeatureEventName = (typeof BetaFeatureEvents)[keyof typeof BetaFeatureEvents];

// ── Aggregate ───────────────────────────────────────────────────────

export type EventName =
    | OpportunityEventName
    | UserEventName
    | TenantEventName
    | AccessEventName
    | SystemEventName
    | SyncEventName
    | AuthEventName
    | InviteEventName
    | PreflightEventName
    | WorkspaceEventName
    | MarketplaceEventName
    | PersonaEventName
    | PersonaOpportunityEventName
    | BetaFeatureEventName;

// ── Domain Topics ───────────────────────────────────────────────────
// Stable Pub/Sub topics — one per domain, prefixed with NEXUS to
// distinguish from other production topics in shared GCP projects.

export const MarketplaceEvents = {
    ORDER_SENT: 'MARKETPLACE.ORDER_SENT',
    VENDOR_CONNECTED: 'MARKETPLACE.VENDOR_CONNECTED',
    VENDOR_TERMS_UPDATED: 'MARKETPLACE.VENDOR_TERMS_UPDATED',
} as const;

export type MarketplaceEventName = (typeof MarketplaceEvents)[keyof typeof MarketplaceEvents];

export const DomainTopics = {
    USERS: 'NEXUS.USERS',
    TENANTS: 'NEXUS.TENANTS',
    ACCESS: 'NEXUS.ACCESS',
    OPPORTUNITIES: 'NEXUS.OPPORTUNITIES',
    SYSTEM: 'NEXUS.SYSTEM',
    DATA_SYNC: 'NEXUS.DATA_SYNC',
    AUTH: 'NEXUS.AUTH',
    INVITES: 'NEXUS.INVITES',
    PREFLIGHT: 'NEXUS.PREFLIGHT',
    WORKSPACE: 'NEXUS.WORKSPACE',
    MARKETPLACE: 'NEXUS.MARKETPLACE',
    PERSONAS: 'NEXUS.PERSONAS',
    BETA_FEATURES: 'NEXUS.BETA_FEATURES',
} as const;

export type DomainTopic = (typeof DomainTopics)[keyof typeof DomainTopics];

/**
 * Logical topic name for storyboard generation.
 *
 * NOTE: Pub/Sub routing in Nexus is done via DOMAIN topics
 * (`DomainTopics.OPPORTUNITIES`) + the `eventType` message attribute
 * (`OpportunityEvents.STORYBOARD_GENERATION_REQUESTED`) — adding a new
 * event never provisions a new Pub/Sub topic. This constant exists as
 * the stable logical identifier for the storyboard pipeline stage; it is
 * NOT a physical Pub/Sub topic. Publish/subscribe uses
 * `OpportunityEvents.STORYBOARD_GENERATION_REQUESTED`, which resolves to
 * `DomainTopics.OPPORTUNITIES` via `eventToDomainTopic`.
 */
export const STORYBOARD_GENERATION_TOPIC = 'opportunity-storyboard-generation' as const;

/** Maps an event-name prefix to its domain topic. */
const PREFIX_TO_DOMAIN: Record<string, DomainTopic> = {
    USER: DomainTopics.USERS,
    TENANT: DomainTopics.TENANTS,
    ACCESS: DomainTopics.ACCESS,
    OPPORTUNITY: DomainTopics.OPPORTUNITIES,
    SYSTEM: DomainTopics.SYSTEM,
    DATA_SYNC: DomainTopics.DATA_SYNC,
    AUTH: DomainTopics.AUTH,
    INVITE: DomainTopics.INVITES,
    PREFLIGHT: DomainTopics.PREFLIGHT,
    WORKSPACE: DomainTopics.WORKSPACE,
    MARKETPLACE: DomainTopics.MARKETPLACE,
    PERSONA: DomainTopics.PERSONAS,
    // `persona_opportunities` events publish to the opportunities domain topic.
    PERSONA_OPPORTUNITY: DomainTopics.OPPORTUNITIES,
    // Backend Beta Feature events only — frontend-only features publish nothing.
    BETA_FEATURE: DomainTopics.BETA_FEATURES,
};

// ── Known event catalog ─────────────────────────────────────────────

/**
 * Every event name in the catalog, flattened. This backs surfaces that let an
 * operator pick any real event — e.g. the publish dialog's from-scratch mode.
 * Unlike `TRIGGERABLE_EVENTS` it carries no payload schema: a consumer that
 * accepts one of these names accepts whatever payload comes with it.
 */
export const KNOWN_EVENT_NAMES: readonly EventName[] = [
    ...Object.values(OpportunityEvents),
    ...Object.values(UserEvents),
    ...Object.values(TenantEvents),
    ...Object.values(AccessEvents),
    ...Object.values(InviteEvents),
    ...Object.values(SystemEvents),
    ...Object.values(AuthEvents),
    ...Object.values(SyncEvents),
    ...Object.values(PreflightEvents),
    ...Object.values(WorkspaceEvents),
    ...Object.values(MarketplaceEvents),
    ...Object.values(PersonaEvents).filter(
        (name) => name !== PersonaEvents.CORE_SYNC && name !== PersonaEvents.INDUSTRY_SYNC,
    ),
    ...Object.values(PersonaOpportunityEvents),
    ...(Object.values(BetaFeatureEvents) as readonly EventName[]),
];

export function isKnownEventName(name: string): name is EventName {
    return (KNOWN_EVENT_NAMES as readonly string[]).includes(name);
}

/**
 * Resolves the domain topic for a given event name.
 * e.g. 'USER.REGISTERED' → 'NEXUS.USERS'
 */
export function eventToDomainTopic(eventName: string): DomainTopic {
    if (typeof eventName !== 'string' || eventName.length === 0) {
        throw new Error(
            'eventToDomainTopic called with an empty event name — rebuild @nexus/contracts and restart services',
        );
    }
    const prefix = eventName.split('.')[0];
    const topic = PREFIX_TO_DOMAIN[prefix];
    if (!topic) {
        throw new Error(`Unknown event prefix "${prefix}" in event "${eventName}". Register it in PREFIX_TO_DOMAIN.`);
    }
    return topic;
}
